/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { keyframes } from '@emotion/react'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'

/**
 * The Player Finder sub-tab's cold-load placeholder. Mirrors RecruitingSettingsCard
 * — title and visibility line, the recruiting switch row, the positions field and
 * the Save action — so the card lands without the layout jumping.
 */
const TeamPlayerFinderSkeleton: React.FC = () => (
  <div
    css={cardStyle}
    data-testid='team-player-finder-skeleton'
    aria-hidden='true'
  >
    <div css={headerStyle}>
      <div css={[bar, titleBarStyle]} />
      <div css={[bar, chipBarStyle]} />
    </div>
    <div css={switchRowStyle}>
      <div css={labelColumnStyle}>
        <div css={[bar, labelBarStyle]} />
        <div css={[bar, hintBarStyle]} />
      </div>
      <div css={[bar, switchBarStyle]} />
    </div>
    <div css={labelColumnStyle}>
      <div css={[bar, labelBarStyle]} />
      <div css={[bar, inputBarStyle]} />
    </div>
    <div css={footerStyle}>
      <div css={[bar, buttonBarStyle]} />
    </div>
  </div>
)

export default TeamPlayerFinderSkeleton

// ---- styled ----
const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 0.9; }
  100% { opacity: 0.5; }
`

// SECONDARY_350 is absent from the typed appColors inference — read it via a
// cast, the established pattern for the house pulse.
const palette = colors as Record<string, string>

const bar = css`
  border-radius: 8px;
  background-color: ${palette.SECONDARY_350};
  animation: ${pulse} 1.4s ease-in-out infinite;
`

const cardStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  background: ${colors.SECONDARY_300};
`

const headerStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const titleBarStyle = css`
  width: 240px;
  max-width: 60%;
  height: 24px;
`

const chipBarStyle = css`
  width: 160px;
  height: 24px;
`

const switchRowStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
`

const labelColumnStyle = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
`

const labelBarStyle = css`
  width: 140px;
  height: 16px;
`

const hintBarStyle = css`
  width: 320px;
  max-width: 100%;
  height: 12px;
`

const switchBarStyle = css`
  width: 32px;
  height: 16px;
`

const inputBarStyle = css`
  width: 100%;
  height: 40px;
`

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
`

const buttonBarStyle = css`
  width: 96px;
  height: 40px;
`
